/* eslint-disable react/prop-types */

import React, { useState, lazy, useEffect, Suspense, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Routes, Route, useLocation } from 'react-router-dom';
import ErrorBoundary from './components/ErrorBoundary/ErrorBoundary';
import LoadingScreen from './components/LoadingScreen/LoadingScreen';
import { safeEventListener } from './utils/asyncErrorHandler.js';

// Lazy-loaded all components with prefetch
const Navbar = lazy(() => import(/* webpackPrefetch: true */ "./components/Navbar/Navbar"));
const Footer = lazy(() => import(/* webpackPrefetch: true */ './components/Footer/Footer'));
const FixedQuoteButton = lazy(() => import(/* webpackPrefetch: true */ './components/QuoteRequest/FixedQuoteButton'));
const ServicesDropdown = lazy(() => import(/* webpackPrefetch: true */ './components/Navbar/Dropdowns/ServicesDropdown'));
const SolutionDetailPage = lazy(() => import(/* webpackPrefetch: true */ "./screens/SolutionDetails/SolutionDetailPage"));
const NotFound = lazy(() => import(/* webpackPrefetch: true */ "./components/NotFound/NotFound"));
const NosServices = lazy(() => import(/* webpackPrefetch: true */ './components/NosServices/NosServices'));
const Apropos = lazy(() => import(/* webpackPrefetch: true */ './screens/Apropos/Apropos'));
const HomePropos = lazy(() => import(/* webpackPrefetch: true */ './components/HomePropos/HomePropos'));
const NosValeurs = lazy(() => import(/* webpackPrefetch: true */ './components/NosValeurs/NosValeurs'));
const NosSolutions = lazy(() => import(/* webpackPrefetch: true */ './components/NosSolutions/NosSolutions'));
const Portfolio = lazy(() => import(/* webpackPrefetch: true */ './components/Portfolio/Portfolio'));
const NosReferences = lazy(() => import(/* webpackPrefetch: true */ './components/NosReferences/NosReferences'));
const Header = lazy(() => import(/* webpackPrefetch: true */ './components/Header/Header'));
const ChiffresClS = lazy(() => import(/* webpackPrefetch: true */ './components/ChiffreCls/ChiffresCls'));

// Single generic template renders every service detail page (content is admin-managed)
const ServiceDetailPage = lazy(() => import(/* webpackPrefetch: true */ './screens/ServiceDetails/ServiceDetailPage'));


//clients and partners components 
const Clients = lazy(() => import(/* webpackPrefetch: true */ './screens/References/Clients'));
const Partners = lazy(() => import(/* webpackPrefetch: true */ './screens/References/Partners'));

const Background = React.memo(({ onLoad }) => {
  const [imageError, setImageError] = useState(false);

  const handleLoad = () => {
    console.log('Background image loaded successfully');
    if (onLoad) {
      try {
        onLoad();
      } catch (error) {
        console.warn('Error in background image onLoad callback:', error);
      }
    }
  };

  const handleError = (e) => {
    console.error('Background image failed to load:', e);
    setImageError(true);
  };

  return (
    <div 
      className="fixed top-0 left-0 w-full h-full -z-10" 
      style={{ 
        backgroundColor: '#242424a2',
        zIndex: -10
      }}
    >
      {/* Background image with lower priority to not interfere with LCP */}
      <img 
        src="https://res.cloudinary.com/dlaxqzp2u/image/upload/f_auto,q_auto,w_1920,h_1080,c_fill/bggco_cbo8ma"
        alt="Background"
        loading="lazy"
        fetchpriority="low"
        onLoad={handleLoad}
        onError={handleError}
        style={{ 
          width: '100vw', 
          height: '100vh',
          objectFit: 'cover',
          display: 'block',
          position: 'absolute',
          top: 0,
          left: 0
        }}
      />
      
      {imageError && (
        <div 
          style={{ 
            position: 'absolute',
            top: 20,
            left: 20,
            backgroundColor: 'rgba(255,0,0,0.8)',
            color: 'white',
            padding: '10px',
            borderRadius: '5px',
            zIndex: 1000
          }}
        >
          Background image failed to load: bggco_cbo8ma
        </div>
      )}
    </div>
  );
});

const RouteFallback = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="w-8 h-8 border-2 border-red-600 border-t-transparent rounded-full animate-spin"></div>
  </div>
);

const MainContent = React.memo(({ language, onContentLoad }) => {
  const loadedComponentsRef = useRef(0);
  const totalComponents = 6; // Adjust based on your main route components
  
  const handleComponentLoad = useCallback(() => {
    loadedComponentsRef.current += 1;
    if (loadedComponentsRef.current >= totalComponents) {
      onContentLoad();
    }
  }, [onContentLoad, totalComponents]);

  return (
    <div style={{ contain: 'layout' }}>
      <Routes>
        <Route path="/" element={
          <Suspense fallback={<RouteFallback />}>
            <Header language={language} onLoad={handleComponentLoad} />
            <HomePropos language={language} onLoad={handleComponentLoad} />
            <NosServices language={language} onLoad={handleComponentLoad} />
            <NosValeurs language={language} onLoad={handleComponentLoad} />
            <NosSolutions language={language} onLoad={handleComponentLoad} />
            
            <NosReferences language={language} onLoad={handleComponentLoad} />
            <ChiffresClS language={language} onLoad={handleComponentLoad} />
            <Portfolio language={language} onLoad={handleComponentLoad} />
            
          </Suspense>
        } />
      <Route path="/apropos" element={
        <Suspense fallback={<RouteFallback />}>
          <Apropos language={language} onLoad={onContentLoad} />
        </Suspense>
      } />
      {/* Single generic template for every service/solution detail page — content is admin-managed */}
      <Route path="/services/:slug" element={
        <Suspense fallback={<RouteFallback />}>
          <ServiceDetailPage />
        </Suspense>
      } />
      <Route path="/solution/:slug" element={
        <Suspense fallback={<RouteFallback />}>
          <SolutionDetailPage />
        </Suspense>
      } />
      <Route path="/references" element={
        <Suspense fallback={<RouteFallback />}>
          <NosReferences language={language} onLoad={onContentLoad} />
        </Suspense>
      } />
      <Route path="/clients" element={
        <Suspense fallback={<RouteFallback />}>
          <Clients language={language} onLoad={onContentLoad} />
        </Suspense>
      } />
      <Route path="/partners" element={
        <Suspense fallback={<RouteFallback />}>
          <Partners language={language} onLoad={onContentLoad} />
        </Suspense>
      } />
      {/* 404 Catch-all route */}
      <Route path="*" element={
        <Suspense fallback={<RouteFallback />}>
          <NotFound />
        </Suspense>
      } />
    </Routes>
    </div>
  );
});

function App() {
  const [language, setLanguage] = useState("fr");
  const [isLoading, setIsLoading] = useState(false);
  const [showServicesDropdown, setShowServicesDropdown] = useState(false);
  const location = useLocation();
  const timerRef = useRef(null);

  useEffect(() => {
    // Close services dropdown when route changes
    setShowServicesDropdown(false);

    // A nav link (e.g. "Contactez-nous" from another page) asked us to land
    // on a specific section instead of the top — don't fight that scroll.
    if (location.state?.scrollTo) return;

    // Enhanced and more aggressive scroll to top when navigating
    const scrollToTop = () => {
      // Force scroll to top immediately with multiple methods
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      window.scrollTo(0, 0);
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      
      // Target all possible scrolling containers
      const scrollContainers = [
        document.querySelector('[style*="overflow"]'),
        document.querySelector('[style*="overflowY: auto"]'),
        document.querySelector('.relative.z-10'),
        document.getElementById('root'),
        ...document.querySelectorAll('[style*="scroll"]'),
        ...document.querySelectorAll('.overflow-auto'),
        ...document.querySelectorAll('.overflow-y-auto')
      ];
      
      scrollContainers.forEach(container => {
        if (container) {
          container.scrollTop = 0;
          container.scrollLeft = 0;
        }
      });
      
      // Force reflow to ensure scroll happens
      document.body.offsetHeight;
    };
    
    // Clear any navigation state that might interfere
    window.history.replaceState({}, document.title, location.pathname);
    
    // Execute scroll to top immediately and with multiple intervals
    scrollToTop();
    setTimeout(scrollToTop, 0);
    setTimeout(scrollToTop, 10);
    setTimeout(scrollToTop, 50);
    setTimeout(scrollToTop, 100);
    setTimeout(scrollToTop, 200);
    setTimeout(scrollToTop, 300);
    setTimeout(scrollToTop, 500);
  }, [location.pathname]);

  // Scroll to top on initial page load/refresh - Simplified version
  useEffect(() => {
    if (location.state?.scrollTo) return;

    // Simple and reliable scroll to top on mount/refresh
    const forceScrollToTop = () => {
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      window.scrollTo(0, 0);
    };

    // Execute scroll to top
    forceScrollToTop();
  }, [location.pathname, location.state?.scrollTo]);

  // A nav link asked us to land on a specific section of the homepage (e.g.
  // "Contactez-nous" from another page should scroll to the footer contact
  // section). Wait for the loading screen to clear and the target element to
  // exist, then scroll to it and forget the request.
  useEffect(() => {
    const targetId = location.state?.scrollTo;
    if (location.pathname !== '/' || !targetId || isLoading) return;

    let cancelled = false;
    let attempts = 0;

    const tryScroll = () => {
      if (cancelled) return;
      const element = document.getElementById(targetId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        window.history.replaceState({}, document.title, location.pathname);
      } else if (attempts < 30) {
        attempts += 1;
        setTimeout(tryScroll, 100);
      }
    };

    const initialDelay = setTimeout(tryScroll, 50);
    return () => {
      cancelled = true;
      clearTimeout(initialDelay);
    };
  }, [location.pathname, location.state, isLoading]);

  useEffect(() => {
    // Aggressive scroll to top for route changes - execute immediately
    const forceScrollToTop = () => {
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      window.scrollTo(0, 0);
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      
      // Target all scrollable elements
      const allScrollableElements = [
        ...document.querySelectorAll('[style*="overflow"]'),
        ...document.querySelectorAll('[style*="scroll"]'),
        ...document.querySelectorAll('.overflow-auto'),
        ...document.querySelectorAll('.overflow-y-auto'),
        document.querySelector('.relative.z-10')
      ];
      
      allScrollableElements.forEach(element => {
        if (element) {
          element.scrollTop = 0;
          element.scrollLeft = 0;
        }
      });
    };
    
    // Execute immediately - this is the most important one for navigation
    // (unless a nav link asked to land on a specific section instead of the top)
    if (!location.state?.scrollTo) forceScrollToTop();

    setIsLoading(true);

    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    timerRef.current = setTimeout(() => {
      setIsLoading(false);
      // Execute scroll to top again after loading completes
      if (!location.state?.scrollTo) forceScrollToTop();
    }, 1500);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [location.pathname]);

  const handleLanguageChange = useCallback((lang) => {
    setLanguage(lang);
  }, []);

  const handleContentLoad = useCallback(() => {
    // Content loaded callback - can be used for analytics or other tracking
  }, []);

  const toggleServicesDropdown = useCallback(() => {
    setShowServicesDropdown(prev => !prev);
  }, []);

  const closeServicesDropdown = useCallback(() => {
    setShowServicesDropdown(false);
  }, []);

  return (
    <ErrorBoundary>
      {/* Rendered via a portal straight onto <body>, outside every wrapper
          div App owns — no ancestor's CSS (contain, overflow, transform...)
          can ever turn its `position: fixed` into something that scrolls
          away, on any page length or device. */}
      {!isLoading &&
        createPortal(
          <Suspense fallback={null}>
            <Navbar
              onLanguageChange={handleLanguageChange}
              onServicesToggle={toggleServicesDropdown}
              showServicesDropdown={showServicesDropdown}
            />
            {showServicesDropdown && (
              <ServicesDropdown
                isOpen={showServicesDropdown}
                onClose={closeServicesDropdown}
              />
            )}
            <FixedQuoteButton />
          </Suspense>,
          document.body,
        )}
      <div
        className="relative min-h-screen"
        style={{
          height: '100vh',
          overflow: 'hidden',
          contain: 'style'
        }}
      >
        {isLoading && <LoadingScreen />}

        <Background />

      <div
        className={`relative z-10 ${!isLoading ? 'pt-20 md:pt-24 pb-1' : ''}`}
        style={{ 
          opacity: isLoading ? 0 : 1, 
          transition: 'opacity 200ms ease-in-out',
          willChange: 'opacity',
          height: 'calc(100vh - 0px)',
          overflowY: 'auto',
          overflowX: 'hidden',
          contain: 'layout'
        }}
      >
        <MainContent language={language} onContentLoad={handleContentLoad} />
        
        <Suspense fallback={null}>
          <Footer language={language} />
        </Suspense>
      </div>
      
      </div>
    </ErrorBoundary>
  );
}

export default React.memo(App);